// @file Stores that handles Plans and Upgrade modals
import { __ } from 'bits/intl'
import { Plan as PlanApi, Billing as BillingApi } from 'dashboard/padlet_api'
import { fetchJson, HTTPMethod } from '@padlet/fetch'
import { EstimateSwitchingCostResult, Plan } from 'types'
import { Module } from 'vuex'
import getCsrfToken from 'bits/csrf_token'
import { navigateTo } from 'bits/location'
import { THINKING_FACE_ICON } from 'bits/confirmation_dialog'
import { ConfirmationDialogLayoutVariant } from 'vuexstore/modules/global_confirmation_dialog'
import { poll } from 'bits/polling'

enum PadletQuota {
  Neon = 5,
  Gold = 20,
  Platinum = Infinity,
}

enum UpgradeStep {
  BelowPadletQuota = 'BelowPadletQuota',
  AtPadletQuota = 'AtPadletQuota',
  OverPadletQuota = 'OverPadletQuota',
  OverFileSizeQuota = 'OverFileSizeQuota',
  SchedulePro = 'SchedulePro',
  ConfirmProMonthly = 'ConfirmProMonthly',
  ConfirmProAnnual = 'ConfirmProAnnual',
  // New values for Gold plan feature
  ScheduleGold = 'ScheduleGold',
  SchedulePlatinum = 'SchedulePlatinum',
  EstimatePlatinumCost = 'EstimatePlatinumCost',
  ConfirmMonthlyPlatinum = 'ConfirmMonthlyPlatinum',
  ConfirmAnnualPlatinum = 'ConfirmAnnualPlatinum',
}

interface PlansState {
  plans: {
    platinum: { annual: {} | Plan; monthly: {} | Plan }
    gold: { annual: {} | Plan; monthly: {} | Plan }
  }
  xUpgradeModal: boolean
  upgradeStep: UpgradeStep | null
  upgradeError: string
  upgradeStatusCheckUrl: string
  isProcessingUpgrade: boolean
  estimatedUpgradeCost: EstimateSwitchingCostResult | null
}

const blankState = (): PlansState => ({
  plans: {
    platinum: { annual: {}, monthly: {} },
    gold: { annual: {}, monthly: {} },
  },
  xUpgradeModal: false,
  upgradeStep: null,
  upgradeError: '',
  upgradeStatusCheckUrl: '',
  isProcessingUpgrade: false,
  estimatedUpgradeCost: null,
})

const PlansModule: Module<PlansState, object> = {
  namespaced: true,
  state: blankState,
  getters: {
    xUpgradeModal: (state) => state.xUpgradeModal,
    upgradeStep: (state) => state.upgradeStep,
    plans: (state) => state.plans,
    plansFetched: (state) => Object.keys(state.plans.platinum.annual).length !== 0,
    upgradeError: (state): string => state.upgradeError,
    upgradeStatusCheckUrl: (state): string => state.upgradeStatusCheckUrl,
    isProcessingUpgrade: (state): boolean => state.isProcessingUpgrade,
    estimatedUpgradeCost: (state): EstimateSwitchingCostResult | null => state.estimatedUpgradeCost,
  },
  mutations: {
    FETCHED_PLANS(state, plans: Plan[]) {
      // Convert a list to a hash that is easy to use in our component
      const hashedPlans = {
        platinum: { annual: {}, monthly: {} },
        gold: { annual: {}, monthly: {} },
      }
      for (const plan of plans) {
        hashedPlans[plan.tier][plan.period] = plan
      }
      state.plans = hashedPlans
    },
    SHOW_PLAN_UPGRADE_MODAL(state, step: UpgradeStep): void {
      state.xUpgradeModal = true
      state.upgradeStep = step
    },
    CLOSE_PLAN_UPGRADE_MODAL(state): void {
      state.xUpgradeModal = false
      state.upgradeStep = null
    },
    SET_ESTIMATED_UPGRADE_COST(state, estimatedUpgradeCost: EstimateSwitchingCostResult): void {
      state.estimatedUpgradeCost = estimatedUpgradeCost
    },
    SET_UPGRADE_ERROR(state, upgradeError: string): void {
      state.upgradeError = upgradeError
    },
    SET_UPGRADE_STATUS_CHECK_URL(state, upgradeStatusCheckUrl: string): void {
      state.upgradeStatusCheckUrl = upgradeStatusCheckUrl
    },
    SET_IS_PROCESSING_UPGRADE(state, isProcessingUpgrade: boolean): void {
      state.isProcessingUpgrade = isProcessingUpgrade
    },
  },
  actions: {
    async fetchPlans({ commit }): Promise<void> {
      const response = await PlanApi.fetch()
      commit('FETCHED_PLANS', response.data)
    },
    showPlanUpgradeModal({ commit, rootGetters, dispatch }, step: UpgradeStep): void {
      if (step === UpgradeStep.ScheduleGold && rootGetters.user.quota.walls_used > PadletQuota.Gold) {
        dispatch('showAboveQuotaConfirmationDialog')
        return
      }
      commit('SHOW_PLAN_UPGRADE_MODAL', step)
    },
    showAboveQuotaConfirmationDialog({ rootGetters, dispatch }): void {
      dispatch(
        'confirmationDialog/openConfirmationDialog',
        {
          ...THINKING_FACE_ICON,
          layoutVariant: rootGetters['browser/isPhone']
            ? ConfirmationDialogLayoutVariant.Drawer
            : ConfirmationDialogLayoutVariant.Large,
          title: __('You’re above this tier’s padlet limit'),
          body: __(
            'You currently have %{amount} padlets, but our Gold tier only supports 20. You won’t be able to create new padlets until you are below the Gold tier limit. Consider switching to our Platinum tier instead.',
            { amount: rootGetters.user.quota.walls_used },
          ),
          confirmButtonText: __('Continue with Gold'),
          cancelButtonText: __('Nevermind'),
          afterConfirmActions: [{ type: 'plans/showPlanUpgradeToGoldModal' }],
        },
        { root: true },
      )
    },
    showPlanUpgradeToGoldModal({ commit }): void {
      commit('SHOW_PLAN_UPGRADE_MODAL', UpgradeStep.ScheduleGold)
    },
    closePlanUpgradeModal({ commit }): void {
      commit('CLOSE_PLAN_UPGRADE_MODAL')
    },
    async fetchEstimatedUpgradeCost({ commit, getters, rootGetters }): Promise<void> {
      const body = JSON.stringify({
        plan_id: getters.plans.platinum[rootGetters.normalizedPaymentSchedule].id,
      })
      try {
        commit('SET_ESTIMATED_UPGRADE_COST', await BillingApi.estimateSwitchingCost({ body }))
      } catch {
        commit('SET_UPGRADE_ERROR', __("We're sorry, we were unable to process your request. Please try again later."))
      }
    },
    showUpgradeToPlatinumModal({ commit, rootGetters, dispatch }): void {
      if (rootGetters.billing.tier === 'neon') {
        commit('SHOW_PLAN_UPGRADE_MODAL', UpgradeStep.SchedulePlatinum)
        return
      }
      dispatch('showUpgradeFromGoldToPlatinumModal')
    },
    async showUpgradeFromGoldToPlatinumModal({ commit, rootGetters, getters, dispatch }): Promise<void> {
      commit('SHOW_PLAN_UPGRADE_MODAL', UpgradeStep.EstimatePlatinumCost)
      await dispatch('fetchEstimatedUpgradeCost')
      commit(
        'SHOW_PLAN_UPGRADE_MODAL',
        rootGetters.billing.period === 'month' ? UpgradeStep.ConfirmMonthlyPlatinum : UpgradeStep.ConfirmAnnualPlatinum,
      )
    },
    async upgradeToPlatinum({ getters, rootGetters, commit }): Promise<void> {
      try {
        commit('SET_IS_PROCESSING_UPGRADE', true)
        const {
          links: { status },
        } = await fetchJson('/premium/switch_plan', {
          method: HTTPMethod.post,
          jsonData: {
            switch_type: 'switch_immediately',
            plan_id: getters.plans.platinum[rootGetters.normalizedPaymentSchedule].id,
          },
          csrfToken: getCsrfToken(),
        })
        await poll({
          pollingUrl: status,
          validationCallback: (response) => response.switched,
          options: { intervalSecs: 5, maxAttempts: 10 },
        })
        navigateTo('/memberships/confirmation')
      } catch (e) {
        commit('SET_UPGRADE_ERROR', __(`We're sorry, we were unable to process your request. Please try again later.`))
      } finally {
        commit('SET_IS_PROCESSING_UPGRADE', false)
      }
    },
  },
}

export default PlansModule
export { PadletQuota, UpgradeStep }
